/* eslint-disable react-hooks/exhaustive-deps,import/no-unresolved */
/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useState, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { isIOS, isMobile } from 'react-device-detect';
import { AddAccount } from 'assets/components';
import { exchangeScreensFlow } from '../../constants';
import ModalBodyLayout from '../../../shared/ModalBodyLayout';
import { formatNumber } from '../../../../utilities/formatHelpers';
import { currencies, exchange, walletTypes } from '../../../../utilities/constants';
import { useRate } from '../../../../utilities/sharedHooks/rates';
import { useSetting } from '../../../../utilities/sharedHooks/settings';
import Badge from '../../../../components/Badge';
import { validateNumbers } from '../../../../utilities/formValidation';
import { RATES_QUERY_KEY } from '../../../../utilities/sharedQueries';
import { MEXC_BUY_ESTIMATION, MEXC_SELL_ESTIMATION } from '../../mutations';
import { showAlert } from '../../../../utilities/notifications';

const Exchange = ({
  openScreen,
  form,
  updateForm,
  supportedCurrencies,
  wallets,
  forcedDestinationWallet,
  fromCurrenciesKeys,
  toCurrenciesKeys,
}) => {
  const { t: translate } = useTranslation();

  const sourceInputRef = useRef(null);
  const destinationInputRef = useRef(null);
  const { data: rates } = useQuery(RATES_QUERY_KEY);

  const [sourceAmountError, setSourceAmountError] = useState(false);
  const [destinationAmountError, setDestinationAmountError] = useState(false);

  const [conversionRateDisplayValue, setConversionRateDisplayValue] = useState('N/A');

  const [priceEstimation, setPriceEstimation] = useState('');

  const [sourceInputChanging, updateSourceInputChanging] = useState(false);

  const destinationCurrencyCode = form?.destination?.currency_code;
  const sourceCurrencyCode = form?.source?.currency_code;
  const destinationCurrencyType = form?.destination?.currency_type;
  const sourceCurrencyType = form?.source?.currency_type;
  const sourceWalletCoin = form?.sourceWallet?.coin;
  const sourceExchangeMinValue = form?.source?.exchange_min_value;
  const destinationExchangeMinValue = form?.destination?.exchange_min_value;
  const sourceExchangeMaxValue = form?.source?.exchange_max_value;
  const destinationExchangeMaxValue = form?.destination?.exchange_max_value;
  const sourceWalletAvailableBalance = form?.sourceWallet?.available_balance;

  const canSwapExchange =
    !forcedDestinationWallet &&
    destinationCurrencyCode &&
    sourceCurrencyCode &&
    rates &&
    rates[`${destinationCurrencyCode?.toUpperCase()}${sourceCurrencyCode?.toUpperCase()}`]
      ? rates[`${destinationCurrencyCode?.toUpperCase()}${sourceCurrencyCode?.toUpperCase()}`]
          ?.availableOnExchange
      : false;

  const availableOnExchange =
    sourceCurrencyCode &&
    destinationCurrencyCode &&
    rates &&
    rates[`${sourceCurrencyCode?.toUpperCase()}${destinationCurrencyCode?.toUpperCase()}`]
      ? rates[`${sourceCurrencyCode?.toUpperCase()}${destinationCurrencyCode?.toUpperCase()}`]
          ?.availableOnExchange
      : false;

  const conversionRate = useRate(sourceCurrencyCode, destinationCurrencyCode);

  const conversionRateReverse = useRate(destinationCurrencyCode, sourceCurrencyCode);

  const [sourceAmount, setSourceAmount] = useState('');
  const [destinationAmount, setDestinationAmount] = useState('');

  const [allowCryptoToCryptoExchange] = useSetting(
    'app_features',
    'feature_allow_crypto_to_crypto_exchange'
  );

  const [enableThirdPartyExchange] = useSetting(
    'app_features',
    'feature_enable_third_party_exchange'
  );

  const MEXCSellEstimationTimerRef = useRef();
  const MEXCBuyEstimationTimerRef = useRef();

  const isBXXSourceOrDestination =
    (destinationCurrencyCode === 'bxx' || sourceCurrencyCode === 'bxx') && enableThirdPartyExchange;

  const userWalletKeys = useMemo(() => {
    let _result = [];
    if (wallets && wallets.length > 0) {
      _result = wallets.map((w) => w.coin);
    }

    return _result;
  }, [wallets]);

  const canSwap = useMemo(() => {
    const isDestinationWalletExistInUserWallet =
      userWalletKeys.includes(destinationCurrencyCode) === true;

    const isSourceWalletExistInUserWallet = userWalletKeys.includes(sourceCurrencyCode) === true;

    const isDestinationCurrencyAllowSell =
      supportedCurrencies[destinationCurrencyCode]?.allows_sell_exchange === true;

    const isDestinationCurrencyExchangeForCrypto =
      supportedCurrencies[destinationCurrencyCode]?.exchange_for_crypto === true;

    const isSourceCurrencyExchangeForCrypto =
      supportedCurrencies[sourceCurrencyCode]?.exchange_for_crypto === true;

    const isSourceCurrencyCanCreateWallet =
      supportedCurrencies[sourceCurrencyCode]?.can_create_wallet === true;

    const isSourceCurrencyAllowBuy =
      supportedCurrencies[sourceCurrencyCode]?.allows_buy_exchange === true;

    if (destinationCurrencyType === walletTypes.FIAT) {
      if (
        sourceCurrencyType === walletTypes.INTERNAL ||
        sourceCurrencyType === walletTypes.CRYPTO
      ) {
        return (
          (isDestinationCurrencyAllowSell || isDestinationCurrencyExchangeForCrypto) &&
          isDestinationWalletExistInUserWallet &&
          isSourceCurrencyAllowBuy &&
          (isSourceCurrencyCanCreateWallet || isSourceWalletExistInUserWallet)
        );
      }
    } else if (
      destinationCurrencyType === walletTypes.CRYPTO ||
      destinationCurrencyType === walletTypes.INTERNAL
    ) {
      if (
        !allowCryptoToCryptoExchange &&
        sourceCurrencyType !== walletTypes.INTERNAL &&
        sourceCurrencyType !== walletTypes.CRYPTO
      ) {
        return (
          (isDestinationCurrencyAllowSell || isDestinationCurrencyExchangeForCrypto) &&
          isDestinationWalletExistInUserWallet &&
          (isSourceCurrencyCanCreateWallet || isSourceWalletExistInUserWallet)
        );
      }
      if (allowCryptoToCryptoExchange) {
        if (sourceCurrencyType === walletTypes.FIAT) {
          return (
            (isDestinationCurrencyAllowSell || isDestinationCurrencyExchangeForCrypto) &&
            isDestinationWalletExistInUserWallet &&
            (isSourceCurrencyCanCreateWallet || isSourceWalletExistInUserWallet)
          );
        }
        return (
          (isDestinationCurrencyAllowSell || isDestinationCurrencyExchangeForCrypto) &&
          isDestinationWalletExistInUserWallet &&
          isSourceCurrencyExchangeForCrypto &&
          (isSourceCurrencyCanCreateWallet || isSourceWalletExistInUserWallet)
        );
      }
    }

    return false;
  }, [
    supportedCurrencies,
    userWalletKeys,
    allowCryptoToCryptoExchange,
    sourceCurrencyType,
    destinationCurrencyCode,
    destinationCurrencyType,
    sourceCurrencyCode,
  ]);

  const onClickSwap = () => {
    setSourceAmount('');
    setDestinationAmount('');
    updateForm({
      ...form,
      sourceAmount: form.destinationAmount,
      destinationAmount: form.sourceAmount,
      sourceWallet: form?.destinationWallet,
      destinationWallet: form.sourceWallet,
      source: form?.destination,
      destination: form.source,
    });
  };

  useEffect(() => {
    if (conversionRate && sourceCurrencyCode && destinationCurrencyCode) {
      setConversionRateDisplayValue(
        `1 ${sourceCurrencyCode?.toUpperCase()} ≈ ${formatNumber(
          conversionRate,
          currencies[destinationCurrencyCode?.toUpperCase()].decimal
        )} ${destinationCurrencyCode?.toUpperCase()}`
      );
    } else {
      setConversionRateDisplayValue('N/A');
    }
  }, [sourceCurrencyCode, destinationCurrencyCode, conversionRate]);

  const clearError = (e) => {
    if (!e.target) {
      e.className = e.className.replace('has-error', '');
    } else {
      e.target.className = e.className.replace('has-error', '');
    }
  };

  const exchangeSpreadValue = () => {
    if (
      sourceCurrencyType === walletTypes.CRYPTO &&
      destinationCurrencyType === walletTypes.CRYPTO
    ) {
      return form.source.crypto_crypto_exchange_spread;
    }
    if (sourceCurrencyType === walletTypes.FIAT || destinationCurrencyType === walletTypes.FIAT) {
      return form.source.crypto_fiat_exchange_spread;
    }
    return 0;
  };

  const { mutate: MEXCSellEstimation, isLoading: isMEXCSellEstimationLoading } = useMutation(
    MEXC_SELL_ESTIMATION,
    {
      onSettled: (data, error) => {
        if (error) {
          showAlert({ message: error.message, type: 'danger' });
        } else if (data) {
          setPriceEstimation(data?.priceEstimation);

          setSourceAmount(
            sourceInputChanging
              ? data?.baseQuantity
              : formatNumber(data?.baseQuantity, exchange.CRYPTO_VALUE)
          );
          setDestinationAmount(
            sourceInputChanging
              ? formatNumber(data?.quoteQuantityEstimation, exchange.CRYPTO_VALUE)
              : data?.quoteQuantityEstimation
          );
        }
        clearTimeout(MEXCSellEstimationTimerRef.current);
      },
    }
  );

  const { mutate: MEXCBuyEstimation, isLoading: isMEXCBuyEstimationLoading } = useMutation(
    MEXC_BUY_ESTIMATION,
    {
      onSettled: (data, error) => {
        if (error) {
          showAlert({ message: error.message, type: 'danger' });
        } else if (data) {
          setPriceEstimation(data?.priceEstimation);
          setSourceAmount(
            sourceInputChanging
              ? data?.baseQuantity
              : formatNumber(data?.baseQuantity, exchange.CRYPTO_VALUE)
          );
          setDestinationAmount(
            sourceInputChanging
              ? formatNumber(data?.quoteQuantityEstimation, exchange.CRYPTO_VALUE)
              : data?.quoteQuantityEstimation
          );
        }
        clearTimeout(MEXCBuyEstimationTimerRef.current);
      },
    }
  );

  const handleSourceMaxClick = () => {
    const sourceWalletAmount = formatNumber(
      sourceWalletAvailableBalance * currencies[sourceWalletCoin?.toUpperCase()].conversion,
      currencies[sourceWalletCoin?.toUpperCase()].decimal
    );

    if (parseFloat(sourceWalletAmount) > 0) {
      setSourceAmount(
        formatNumber(
          sourceWalletAvailableBalance * currencies[sourceWalletCoin?.toUpperCase()].conversion,
          currencies[sourceWalletCoin?.toLowerCase()].decimal
        )
      );
    } else {
      setSourceAmount(sourceWalletAmount);
    }

    if (!isBXXSourceOrDestination) {
      const newDestinationAmount =
        sourceWalletAvailableBalance *
        currencies[sourceWalletCoin?.toUpperCase()].conversion *
        conversionRate;
      const fees = exchangeSpreadValue();
      const internalFee = newDestinationAmount * fees;
      const newDestinationAmountMinusFees = newDestinationAmount - internalFee;

      setDestinationAmount(
        formatNumber(newDestinationAmountMinusFees, currencies[destinationCurrencyCode].decimal)
      );
    } else if (isBXXSourceOrDestination && sourceCurrencyCode === 'bxx') {
      if (MEXCSellEstimationTimerRef && MEXCSellEstimationTimerRef.current) {
        clearTimeout(MEXCSellEstimationTimerRef.current);
      }

      MEXCSellEstimationTimerRef.current = setTimeout(() => {
        MEXCSellEstimation({
          source: sourceCurrencyCode?.toUpperCase(),
          destination: destinationCurrencyCode?.toUpperCase(),
          baseAmount: sourceWalletAmount,
        });
      }, 2000);
    } else if (isBXXSourceOrDestination && destinationCurrencyCode === 'bxx') {
      if (MEXCBuyEstimationTimerRef && MEXCBuyEstimationTimerRef.current) {
        clearTimeout(MEXCBuyEstimationTimerRef.current);
      }

      MEXCBuyEstimationTimerRef.current = setTimeout(() => {
        MEXCBuyEstimation({
          source: sourceCurrencyCode?.toUpperCase(),
          destination: destinationCurrencyCode?.toUpperCase(),
          baseAmount: sourceWalletAmount,
        });
      }, 2000);
    }
  };

  const handleOnKeyUpSourceAmountInput = (e) => {
    if (isBXXSourceOrDestination && sourceCurrencyCode === 'bxx') {
      if (MEXCSellEstimationTimerRef && MEXCSellEstimationTimerRef.current) {
        clearTimeout(MEXCSellEstimationTimerRef.current);
      }
      MEXCSellEstimationTimerRef.current = setTimeout(() => {
        if (e.target.value) {
          MEXCSellEstimation({
            source: sourceCurrencyCode?.toUpperCase(),
            destination: destinationCurrencyCode?.toUpperCase(),
            baseAmount: e.target.value,
          });
        }
      }, 1000);
    } else if (isBXXSourceOrDestination && destinationCurrencyCode === 'bxx') {
      if (MEXCBuyEstimationTimerRef && MEXCBuyEstimationTimerRef.current) {
        clearTimeout(MEXCBuyEstimationTimerRef.current);
      }
      MEXCBuyEstimationTimerRef.current = setTimeout(() => {
        if (e.target.value) {
          MEXCBuyEstimation({
            source: sourceCurrencyCode?.toUpperCase(),
            destination: destinationCurrencyCode?.toUpperCase(),
            baseAmount: e.target.value,
          });
        }
      }, 1000);
    }
  };

  const handleSourceAmountInput = (e) => {
    if (!validateNumbers(e.target.value, sourceCurrencyType, false)) {
      setDestinationAmount('');
      return;
    }

    setSourceAmount(e.target.value);

    if (!e.target.value) {
      setDestinationAmount('');
    } else if (!isBXXSourceOrDestination) {
      const newDestinationAmount = e.target.value * conversionRate;
      const fees = exchangeSpreadValue();
      const internalFee = newDestinationAmount * fees;
      const newDestinationAmountMinusFees = newDestinationAmount - internalFee;

      setDestinationAmount(
        formatNumber(newDestinationAmountMinusFees, currencies[destinationCurrencyCode].decimal)
      );
    }
  };

  const handleOnKeyUpDestinationAmountInput = (e) => {
    if (isBXXSourceOrDestination && sourceCurrencyCode === 'bxx') {
      if (MEXCSellEstimationTimerRef && MEXCSellEstimationTimerRef.current) {
        clearTimeout(MEXCSellEstimationTimerRef.current);
      }
      MEXCSellEstimationTimerRef.current = setTimeout(() => {
        if (e.target.value) {
          MEXCSellEstimation({
            source: sourceCurrencyCode?.toUpperCase(),
            destination: destinationCurrencyCode?.toUpperCase(),
            quoteAmount: e.target.value,
          });
        }
      }, 2000);
    } else if (isBXXSourceOrDestination && destinationCurrencyCode === 'bxx') {
      if (MEXCBuyEstimationTimerRef && MEXCBuyEstimationTimerRef.current) {
        clearTimeout(MEXCBuyEstimationTimerRef.current);
      }
      MEXCBuyEstimationTimerRef.current = setTimeout(() => {
        if (e.target.value) {
          MEXCBuyEstimation({
            source: sourceCurrencyCode?.toUpperCase(),
            destination: destinationCurrencyCode?.toUpperCase(),
            quoteAmount: e.target.value,
          });
        }
      }, 2000);
    }
  };

  const handleDestinationAmountInput = (e) => {
    if (!validateNumbers(e.target.value, destinationCurrencyType, false)) {
      setSourceAmount('');
      return;
    }

    setDestinationAmount(e.target.value);

    if (!e.target.value) {
      setSourceAmount('');
    } else if (!isBXXSourceOrDestination) {
      const fees = exchangeSpreadValue();
      const newSourceAmount = e.target.value * conversionRateReverse;
      const internalFee = newSourceAmount * fees;
      const newSourceAmountMinusFees = newSourceAmount - internalFee;

      setSourceAmount(
        formatNumber(newSourceAmountMinusFees, currencies[sourceCurrencyCode].decimal)
      );
    }
  };

  useEffect(() => {
    if (sourceInputRef && sourceInputRef.current) {
      if (sourceInputRef.current.value < sourceExchangeMinValue && sourceAmount) {
        if (sourceInputRef.current.className.indexOf('has-error') === -1) {
          sourceInputRef.current.className += ' has-error';
        }
        setSourceAmountError(true);
      } else if (sourceInputRef.current.value > sourceExchangeMaxValue) {
        if (sourceInputRef.current.className.indexOf('has-error') === -1) {
          sourceInputRef.current.className += ' has-error';
        }
        setSourceAmountError(true);
      } else {
        clearError(sourceInputRef.current);
        setSourceAmountError(false);
      }
    }

    if (destinationInputRef && destinationInputRef.current) {
      if (destinationInputRef.current.value < destinationExchangeMinValue && sourceAmount) {
        if (destinationInputRef.current.className.indexOf('has-error') === -1) {
          destinationInputRef.current.className += ' has-error';
        }
        setDestinationAmountError(true);
      } else if (destinationInputRef.current.value > destinationExchangeMaxValue) {
        if (destinationInputRef.current.className.indexOf('has-error') === -1) {
          destinationInputRef.current.className += ' has-error';
        }
        setDestinationAmountError(true);
      } else {
        clearError(destinationInputRef.current);
        setDestinationAmountError(false);
      }
    }
  }, [
    sourceExchangeMinValue,
    sourceExchangeMaxValue,
    destinationExchangeMinValue,
    destinationExchangeMaxValue,
    sourceAmount,
    destinationAmount,
    sourceInputRef,
    destinationInputRef,
  ]);

  const disableButton = useMemo(
    () =>
      sourceAmountError ||
      destinationAmountError ||
      sourceAmount <= 0 ||
      destinationAmount <= 0 ||
      sourceAmount >
        sourceWalletAvailableBalance * currencies[sourceWalletCoin?.toUpperCase()].conversion ||
      (isBXXSourceOrDestination && isMEXCSellEstimationLoading) ||
      (isBXXSourceOrDestination && isMEXCBuyEstimationLoading),
    [
      sourceAmountError,
      destinationAmountError,
      sourceAmount,
      destinationAmount,
      sourceWalletAvailableBalance,
      sourceWalletCoin,
      isBXXSourceOrDestination,
      isMEXCSellEstimationLoading,
      isMEXCBuyEstimationLoading,
    ]
  );

  const renderFooter = () => (
    <button
      className={`btn ${!disableButton || !canSwapExchange ? 'btn-primary' : 'btn-disabled'} py-3`}
      type="button"
      loading={
        (isBXXSourceOrDestination && isMEXCBuyEstimationLoading) ||
        (isBXXSourceOrDestination && isMEXCSellEstimationLoading)
          ? 'true'
          : 'false'
      }
      disabled={disableButton}
      onClick={() => {
        updateForm({ ...form, sourceAmount, destinationAmount });
        openScreen(exchangeScreensFlow.CONFIRM_EXCHANGE);
      }}>
      {translate('preview_exchange_btn_label')}
    </button>
  );

  const isOneSourceOnlyAvailable = useMemo(() => {
    if (rates && fromCurrenciesKeys) {
      const displayedCurrencies = fromCurrenciesKeys.reduce((acc, item) => {
        const rateKey = `${form.destination?.currency_code?.toUpperCase()}${item?.currency_code?.toUpperCase()}`;

        const currentRate = rates[rateKey];

        if (currentRate && currentRate?.availableOnExchange) {
          return [...acc, item];
        }
        return acc;
      }, []);

      if (displayedCurrencies && displayedCurrencies.length === 1) {
        return displayedCurrencies[0].id === form.source?.id;
      }
    }
    return false;
  }, [rates, fromCurrenciesKeys, form]);

  const isOneDestinationOnlyAvailable = useMemo(() => {
    if (rates && toCurrenciesKeys) {
      const displayedCurrencies = toCurrenciesKeys.reduce((acc, item) => {
        const rateKey = `${form.source.currency_code?.toUpperCase()}${item.currency_code.toUpperCase()}`;

        const currentRate = rates[rateKey];

        if (currentRate && currentRate?.availableOnExchange) {
          return [...acc, item];
        }
        return acc;
      }, []);

      if (displayedCurrencies && displayedCurrencies.length === 1) {
        return displayedCurrencies[0].id === form.destination?.id;
      }
    }
    return false;
  }, [rates, toCurrenciesKeys, form]);

  return (
    <ModalBodyLayout
      renderFooter={renderFooter}
      isScrollable={isMobile}
      title={translate('exchange_title')}>
      <AddAccount
        label={sourceWalletCoin?.toUpperCase()}
        icon={sourceWalletCoin?.toUpperCase()}
        subLabel={`${formatNumber(
          sourceWalletAvailableBalance * currencies[sourceWalletCoin?.toUpperCase()].conversion,
          currencies[sourceWalletCoin?.toUpperCase()].decimal
        )} ${sourceWalletCoin?.toUpperCase()}`}
        isImg
        noMobile
        className="mb-3 mt-5 bg-light"
        imageStyle={{
          height: '1.6rem',
          width: '1.6rem',
          ...(sourceWalletCoin?.toUpperCase() === currencies.XRP.icon
            ? {
                backgroundColor: 'white',
                padding: '1px',
                borderRadius: '20px',
              }
            : {}),
        }}
        disabled={isOneSourceOnlyAvailable}
        withoutAction={isOneSourceOnlyAvailable}
        onClick={() => {
          updateForm({
            ...form,
            sourceAmount,
            isOpenSourceCurrencies: true,
            checkOpenForExchange: false,
          });
          openScreen(exchangeScreensFlow.CURRENCIES);
        }}
      />
      {availableOnExchange && (
        <>
          <div className={`form-group ${isIOS ? 'ios' : ''}`}>
            <div className="w-100 position-relative">
              <input
                ref={sourceInputRef}
                placeholder={translate('you_exchange_label')}
                className={`form-control-input pe-4 ${isIOS ? 'ios' : ''}`}
                value={sourceAmount}
                onChange={handleSourceAmountInput}
                onKeyUp={handleOnKeyUpSourceAmountInput}
                onPointerDown={() => updateSourceInputChanging(true)}
                name="source"
                type="text"
              />
              {sourceAmount < sourceExchangeMinValue && sourceAmount && (
                <label className="error-label">
                  {translate('exchange_min_error_msg').replace(
                    '%d',
                    `${sourceExchangeMinValue} ${sourceCurrencyCode?.toUpperCase()}`
                  )}
                </label>
              )}
              {sourceAmount > sourceExchangeMaxValue && (
                <label className="error-label">
                  {translate('exchange_max_error_msg').replace(
                    '%d',
                    `${sourceExchangeMaxValue} ${sourceCurrencyCode?.toUpperCase()}`
                  )}
                </label>
              )}
              <Badge
                onClick={handleSourceMaxClick}
                withNoActive
                style={{ top: 0, right: 0 }}
                content={translate('max_btn_label')}
                className={`py-1 px-3 text-f6 btn rounded-max border text-white position-absolute text-uppercase input-badge-custom ${
                  isIOS ? 'ios' : ''
                }`}
              />
              <label className="floating-label">{translate('you_exchange_label')}</label>
            </div>
          </div>

          <div className="d-flex flex-row justify-content-center align-items-center my-5 border-1 border-shadow">
            <div
              role="presentation"
              tabIndex={-1}
              className="cursor-pointer rounded-max border border-disabled pb-1 pt-2 px-2"
              onClick={() => {
                if (canSwap && availableOnExchange && canSwapExchange) {
                  onClickSwap();
                }
              }}>
              <i
                label="swap"
                className={`icon-buy-sell-menu ${
                  canSwap && availableOnExchange && canSwapExchange ? 'text-hover' : 'text-disabled'
                } text-f4`}
              />
            </div>
            {!isBXXSourceOrDestination && (
              <p className="text-shadow text-f ms-2">{conversionRateDisplayValue}</p>
            )}
            {isBXXSourceOrDestination &&
              !isMEXCSellEstimationLoading &&
              !isMEXCBuyEstimationLoading &&
              priceEstimation && (
                <p className="text-shadow text-f ms-2">{`1 BXX ≈ ${formatNumber(
                  priceEstimation,
                  exchange.FIAT_LESS_ONE_VALUE
                )} ${
                  sourceCurrencyCode === 'bxx'
                    ? destinationCurrencyCode?.toUpperCase()
                    : sourceCurrencyCode?.toUpperCase()
                }`}</p>
              )}
          </div>

          <AddAccount
            label={destinationCurrencyCode?.toUpperCase()}
            icon={destinationCurrencyCode?.toUpperCase()}
            subLabel={`${formatNumber(
              form?.destinationWallet?.available_balance *
                currencies[destinationCurrencyCode?.toUpperCase()].conversion,
              currencies[destinationCurrencyCode?.toUpperCase()].decimal
            )} ${destinationCurrencyCode?.toUpperCase()}`}
            isImg
            noMobile
            className="my-3 bg-light"
            imageStyle={{
              height: '1.6rem',
              width: '1.6rem',
              ...(destinationCurrencyCode?.toUpperCase() === currencies.XRP.icon
                ? {
                    backgroundColor: 'white',
                    padding: '1px',
                    borderRadius: '20px',
                  }
                : {}),
            }}
            disabled={isOneDestinationOnlyAvailable}
            withoutAction={forcedDestinationWallet || isOneDestinationOnlyAvailable}
            onClick={() => {
              if (!forcedDestinationWallet) {
                updateForm({
                  ...form,
                  sourceAmount,
                  isOpenSourceCurrencies: false,
                  checkOpenForExchange: true,
                });
                openScreen(exchangeScreensFlow.CURRENCIES);
              }
            }}
          />

          <div className={`form-group ${isIOS ? 'ios' : ''}`}>
            <div className="w-100 position-relative">
              <input
                ref={destinationInputRef}
                placeholder={translate('you_get_estimated_label')}
                className={`form-control-input pe-4 ${isIOS ? 'ios' : ''}`}
                value={destinationAmount}
                onChange={handleDestinationAmountInput}
                onKeyUp={handleOnKeyUpDestinationAmountInput}
                onPointerDown={() => updateSourceInputChanging(false)}
                name="destination"
                type="text"
              />
              <label className="floating-label">{translate('you_get_estimated_label')}</label>
              {destinationAmount < destinationExchangeMinValue && sourceAmount && (
                <label className="error-label">
                  {translate('exchange_min_error_msg').replace(
                    '%d',
                    `${destinationExchangeMinValue} ${destinationCurrencyCode?.toUpperCase()}`
                  )}
                </label>
              )}
              {destinationAmount > destinationExchangeMaxValue && (
                <label className="error-label">
                  {translate('exchange_max_error_msg').replace(
                    '%d',
                    `${destinationExchangeMaxValue} ${destinationCurrencyCode?.toUpperCase()}`
                  )}
                </label>
              )}
            </div>
          </div>
        </>
      )}
    </ModalBodyLayout>
  );
};

export default Exchange;

Exchange.propTypes = {
  openScreen: PropTypes.func.isRequired,
  form: PropTypes.object.isRequired,
  updateForm: PropTypes.func.isRequired,
  wallets: PropTypes.array.isRequired,
  fromCurrenciesKeys: PropTypes.array,
  toCurrenciesKeys: PropTypes.array,
  supportedCurrencies: PropTypes.object.isRequired,
  forcedDestinationWallet: PropTypes.object,
};

Exchange.defaultProps = {
  forcedDestinationWallet: null,
  fromCurrenciesKeys: [],
  toCurrenciesKeys: [],
};
